/**
 * Basketeer Wishlist — localStorage-based wishlist system
 * No account required. Stores product handles in localStorage.
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'basketeer_wishlist';
  const CHANGE_EVENT = 'basketeer:wishlist:change';

  /* ──────────────────────────────────────────────
   * Storage helpers
   * ────────────────────────────────────────────── */
  function getWishlist() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function saveWishlist(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      // Storage full or private browsing — fail silently
    }
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { count: list.length } }));
  }

  function isInWishlist(handle) {
    return getWishlist().indexOf(handle) !== -1;
  }

  function addToWishlist(handle) {
    const list = getWishlist();
    if (list.indexOf(handle) === -1) {
      list.push(handle);
      saveWishlist(list);
    }
    return list;
  }

  function removeFromWishlist(handle) {
    let list = getWishlist();
    list = list.filter(function (h) { return h !== handle; });
    saveWishlist(list);
    return list;
  }

  function toggleWishlist(handle) {
    if (isInWishlist(handle)) {
      removeFromWishlist(handle);
      return false;
    }
    addToWishlist(handle);
    return true;
  }

  /* ──────────────────────────────────────────────
   * UI — Heart buttons
   * ────────────────────────────────────────────── */
  function syncAllHearts() {
    document.querySelectorAll('[data-wishlist-handle]').forEach(function (btn) {
      var handle = btn.getAttribute('data-wishlist-handle');
      var active = isInWishlist(handle);
      btn.classList.toggle('wishlist-btn--active', active);
      btn.setAttribute('aria-pressed', String(active));
      btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
    });
  }

  function handleHeartClick(e) {
    var btn = e.target.closest('[data-wishlist-handle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    var handle = btn.getAttribute('data-wishlist-handle');
    var added = toggleWishlist(handle);

    // Animate
    btn.classList.toggle('wishlist-btn--active', added);
    btn.setAttribute('aria-pressed', String(added));
    btn.setAttribute('aria-label', added ? 'Remove from wishlist' : 'Add to wishlist');
    btn.classList.add('wishlist-btn--pop');
    setTimeout(function () {
      btn.classList.remove('wishlist-btn--pop');
    }, 400);
  }

  /* ──────────────────────────────────────────────
   * UI — Header badge
   * ────────────────────────────────────────────── */
  function updateBadge() {
    var count = getWishlist().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = count;
    });
    document.querySelectorAll('[data-wishlist-badge]').forEach(function (el) {
      el.classList.toggle('hidden', count === 0);
    });
  }

  /* ──────────────────────────────────────────────
   * Wishlist page renderer
   * ────────────────────────────────────────────── */
  function renderWishlistPage() {
    var container = document.getElementById('wishlist-page-grid');
    var emptyState = document.getElementById('wishlist-empty');
    var pageActions = document.getElementById('wishlist-page-actions');
    var countEl = document.getElementById('wishlist-page-count');
    if (!container) return; // Not on wishlist page

    var handles = getWishlist();

    if (handles.length === 0) {
      container.style.display = 'none';
      if (pageActions) pageActions.style.display = 'none';
      if (emptyState) emptyState.style.display = '';
      if (countEl) countEl.textContent = '0';
      return;
    }

    if (emptyState) emptyState.style.display = 'none';
    container.style.display = '';
    if (pageActions) pageActions.style.display = '';
    if (countEl) countEl.textContent = String(handles.length);

    // Show loading state
    container.innerHTML = '<div class="wishlist-loading"><div class="wishlist-loading__spinner"></div><p>Loading your wishlist...</p></div>';

    // Fetch all products in parallel
    var fetchPromises = handles.map(function (handle) {
      return fetch('/products/' + handle + '.json')
        .then(function (r) {
          if (!r.ok) throw new Error('Not found');
          return r.json();
        })
        .then(function (data) {
          return data.product;
        })
        .catch(function () {
          return null; // Product no longer exists
        });
    });

    Promise.all(fetchPromises).then(function (products) {
      // Filter out nulls (removed products) and clean up localStorage
      var validProducts = [];
      var validHandles = [];
      products.forEach(function (p, i) {
        if (p) {
          validProducts.push(p);
          validHandles.push(handles[i]);
        }
      });

      // Sync localStorage if some products were removed
      if (validHandles.length !== handles.length) {
        saveWishlist(validHandles);
      }

      if (validProducts.length === 0) {
        container.style.display = 'none';
        if (pageActions) pageActions.style.display = 'none';
        if (emptyState) emptyState.style.display = '';
        if (countEl) countEl.textContent = '0';
        return;
      }

      if (countEl) countEl.textContent = String(validProducts.length);

      container.innerHTML = validProducts.map(function (product) {
        var image = product.image ? product.image.src : '';
        var variant = product.variants && product.variants[0] ? product.variants[0] : {};
        var price = variant.price ? formatMoney(variant.price) : '';
        var comparePrice = variant.compare_at_price && parseFloat(variant.compare_at_price) > parseFloat(variant.price)
          ? formatMoney(variant.compare_at_price)
          : '';
        // All products use inventory_policy:'continue' — always purchasable
        var available = true;
        var variantId = variant.id || '';
        var hasMultipleVariants = product.variants && product.variants.length > 1;
        var productUrl = '/products/' + product.handle;

        return (
          '<div class="wishlist-card" data-wishlist-card="' + product.handle + '">' +
            '<div class="wishlist-card__media">' +
              '<a href="' + productUrl + '">' +
                (image
                  ? '<img src="' + image.replace(/\.([^.]+)$/, '_360x.$1') + '" alt="' + escapeHtml(product.title) + '" loading="lazy" width="360" height="360">'
                  : '<div class="wishlist-card__placeholder"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="1"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg></div>') +
              '</a>' +
              '<button class="wishlist-card__remove" data-wishlist-remove="' + product.handle + '" aria-label="Remove ' + escapeHtml(product.title) + ' from wishlist" type="button">' +
                '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
              '</button>' +
            '</div>' +
            '<div class="wishlist-card__info">' +
              '<h3 class="wishlist-card__title"><a href="' + productUrl + '">' + escapeHtml(product.title) + '</a></h3>' +
              '<div class="wishlist-card__price">' +
                (comparePrice ? '<span class="wishlist-card__compare-price">' + comparePrice + '</span>' : '') +
                '<span class="wishlist-card__current-price">' + price + '</span>' +
              '</div>' +
              '<div class="wishlist-card__actions">' +
                (available
                  ? (hasMultipleVariants
                    ? '<a href="' + productUrl + '" class="wishlist-card__btn wishlist-card__btn--primary">Choose Options</a>'
                    : '<button type="button" class="wishlist-card__btn wishlist-card__btn--primary" data-wishlist-atc="' + variantId + '">Add to Cart</button>')
                  : '<button type="button" class="wishlist-card__btn wishlist-card__btn--disabled" disabled>Sold Out</button>') +
              '</div>' +
            '</div>' +
          '</div>'
        );
      }).join('');
    });
  }

  /* ──────────────────────────────────────────────
   * Helpers
   * ────────────────────────────────────────────── */
  function formatMoney(cents) {
    // Shopify AJAX API returns price as a string like "1500.00" (dollars, not cents)
    var amount = parseFloat(cents);
    if (isNaN(amount)) return '';
    // Detect store currency format from Shopify global
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: window.Shopify.currency.active
      }).format(amount);
    }
    // Fallback: Thai Baht
    return '\u0E3F' + amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /* ──────────────────────────────────────────────
   * Cart operations (wishlist page)
   * ────────────────────────────────────────────── */
  function addToCart(variantId, btn) {
    if (!variantId) return;
    btn.disabled = true;
    btn.textContent = 'Adding...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Failed');
        return r.json();
      })
      .then(function () {
        if (window.BKCart) window.BKCart.invalidate();
        btn.textContent = 'Added!';
        btn.classList.add('wishlist-card__btn--success');
        updateCartBubble();
        setTimeout(function () {
          btn.textContent = 'Add to Cart';
          btn.classList.remove('wishlist-card__btn--success');
          btn.disabled = false;
        }, 1500);
      })
      .catch(function () {
        btn.textContent = 'Error';
        setTimeout(function () {
          btn.textContent = 'Add to Cart';
          btn.disabled = false;
        }, 1500);
      });
  }

  function addAllToCart() {
    var buttons = document.querySelectorAll('[data-wishlist-atc]');
    if (buttons.length === 0) return;

    var items = [];
    buttons.forEach(function (btn) {
      var vid = btn.getAttribute('data-wishlist-atc');
      if (vid && !btn.disabled) {
        items.push({ id: parseInt(vid, 10), quantity: 1 });
      }
    });

    if (items.length === 0) return;

    var addAllBtn = document.getElementById('wishlist-add-all');
    if (addAllBtn) {
      addAllBtn.disabled = true;
      addAllBtn.textContent = 'Adding...';
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Failed');
        return r.json();
      })
      .then(function () {
        if (window.BKCart) window.BKCart.invalidate();
        if (addAllBtn) {
          addAllBtn.textContent = 'Added All!';
          addAllBtn.classList.add('wishlist-card__btn--success');
          updateCartBubble();
          setTimeout(function () {
            addAllBtn.textContent = 'Add All to Cart';
            addAllBtn.classList.remove('wishlist-card__btn--success');
            addAllBtn.disabled = false;
          }, 2000);
        }
      })
      .catch(function () {
        if (addAllBtn) {
          addAllBtn.textContent = 'Error — try again';
          setTimeout(function () {
            addAllBtn.textContent = 'Add All to Cart';
            addAllBtn.disabled = false;
          }, 2000);
        }
      });
  }

  function paintCartBubble(cart) {
    if (!cart || typeof cart.item_count !== 'number') return;
    document.querySelectorAll('.cart-count-bubble span, [data-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count;
    });
    document.querySelectorAll('.cart-count-bubble').forEach(function (b) {
      b.classList.remove('hidden');
    });
  }

  function updateCartBubble() {
    // Shared reader (window.BKCart) — de-duped + 429 backoff. Falls back to a
    // direct fetch if the snippet somehow did not load.
    if (window.BKCart) {
      window.BKCart.get().then(paintCartBubble);
      return;
    }
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(paintCartBubble)
      .catch(function (err) { console.warn('[Wishlist] cart bubble update failed', err); });
  }

  /* ──────────────────────────────────────────────
   * Event delegation
   * ────────────────────────────────────────────── */
  document.addEventListener('click', function (e) {
    // Heart toggle
    if (e.target.closest('[data-wishlist-handle]')) {
      handleHeartClick(e);
      return;
    }

    // Remove from wishlist page
    var removeBtn = e.target.closest('[data-wishlist-remove]');
    if (removeBtn) {
      e.preventDefault();
      var handle = removeBtn.getAttribute('data-wishlist-remove');
      var card = document.querySelector('[data-wishlist-card="' + handle + '"]');
      if (card) {
        card.classList.add('wishlist-card--removing');
        setTimeout(function () {
          removeFromWishlist(handle);
          syncAllHearts();
          renderWishlistPage();
          updateBadge();
        }, 300);
      } else {
        removeFromWishlist(handle);
        syncAllHearts();
        renderWishlistPage();
        updateBadge();
      }
      return;
    }

    // Add to cart from wishlist page
    var atcBtn = e.target.closest('[data-wishlist-atc]');
    if (atcBtn) {
      e.preventDefault();
      addToCart(atcBtn.getAttribute('data-wishlist-atc'), atcBtn);
      return;
    }

    // Add all to cart
    if (e.target.closest('#wishlist-add-all')) {
      e.preventDefault();
      addAllToCart();
      return;
    }

    // Clear wishlist
    if (e.target.closest('#wishlist-clear-all')) {
      e.preventDefault();
      if (confirm('Remove all items from your wishlist?')) {
        saveWishlist([]);
        syncAllHearts();
        renderWishlistPage();
        updateBadge();
      }
      return;
    }

    // Card image clicks are handled by Dawn's native ::after overlay on
    // .card__heading a (z-index:1 covering .card-wrapper). We removed the
    // stacking context from .card__inner.gradient so hearts/buttons at
    // higher z-index are clickable above the overlay.
  });

  /* ──────────────────────────────────────────────
   * Cross-tab sync
   * ────────────────────────────────────────────── */
  window.addEventListener('storage', function (e) {
    if (e.key === STORAGE_KEY) {
      syncAllHearts();
      updateBadge();
      renderWishlistPage();
    }
  });

  /* ──────────────────────────────────────────────
   * Init
   * ────────────────────────────────────────────── */
  function init() {
    syncAllHearts();
    updateBadge();
    renderWishlistPage();
  }

  // Listen for wishlist changes (from other parts of code)
  window.addEventListener(CHANGE_EVENT, function () {
    updateBadge();
  });

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-sync when Shopify section rendering replaces DOM (e.g. infinite scroll, AJAX)
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function () {
      setTimeout(syncAllHearts, 100);
    });
  }

  // Expose for external use if needed
  window.BasketeerWishlist = {
    get: getWishlist,
    add: addToWishlist,
    remove: removeFromWishlist,
    toggle: toggleWishlist,
    isIn: isInWishlist,
    sync: syncAllHearts,
    updateBadge: updateBadge
  };
})();
